import { EPaymentFrequency } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import {
  Banner,
  BodyText,
  ControlledDateOnlyPicker,
  ControlledSelect,
  FieldGrid,
  FormMethods,
  useFormDialog,
  useIfUser,
} from '@if/mui'
import { Stack } from '@mui/material'
import { getToday } from '@src/services/DateTimeProvider'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { EPaymentMethod, useTranslatedPaymentFrequency } from '@src/types/constants'
import {
  CalendarSimulationAction,
  ECalendarSimulationAction,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { CalendarSync } from 'lucide-react'
import { JSX, useMemo } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useCalendarPreviewConfirm } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-confirm'
import {
  useLoanCalendarActionPreview,
  useSettledValue,
} from '../LoanCalendarActionPreview/LoanCalendarActionPreview-recompute'
import LoanCalendarActionPreviewBody from '../LoanCalendarActionPreview/LoanCalendarActionPreviewBody'
import { useShouldDisableDateForNewFirstPayment } from './LoanCalendarFrequency-hooks'
import { NewFrequencySchemaType, useNewFrequencySchema } from './LoanCalendarFrequency-schema'
import { getLoanCalendarMaxPaymentDate } from './LoanCalendarFrequency-selectors'

interface LoanCalendarFrequencyBodyProps {
  form: FormMethods<NewFrequencySchemaType>
  initialSignedSimulation: SignedCalendarSimulation
  frequencyOptions: JSX.Element[]
  disabled: boolean
}

const LoanCalendarFrequencyBody = ({
  form,
  initialSignedSimulation,
  frequencyOptions,
  disabled,
}: LoanCalendarFrequencyBodyProps) => {
  const { t } = useTranslation()
  const { control } = form
  const originalSimulation = initialSignedSimulation.simulation

  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments

  const frequency = useWatch({ control, name: 'frequency' })
  const previewedSimulation = useWatch({ control, name: 'previewedSimulation' })
  const firstPaymentDate = useWatch({ control, name: 'firstPaymentDate' })
  const { isValid } = useFormState({ control })

  // The recompute follows the fields once they settle, and settling is what opens Confirm —
  // so what the agent is about to apply is always the calendar they are looking at.
  const settledFrequency = useSettledValue(frequency)
  const settledFirstPaymentDate = useSettledValue(firstPaymentDate)

  const action = useMemo<CalendarSimulationAction | null>(
    () =>
      isValid && settledFrequency && settledFirstPaymentDate
        ? {
            actionType: ECalendarSimulationAction.UpdateFrequency,
            frequency: settledFrequency,
            firstPaymentDate: settledFirstPaymentDate,
          }
        : null,
    [isValid, settledFrequency, settledFirstPaymentDate],
  )

  const error = useLoanCalendarActionPreview({ form, initialSignedSimulation, action })

  const minFirstPaymentDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(
        EPaymentMethod.PreAuthorizedDebit,
        originalSimulation.fullLoan.loan.activationOn,
        canBypassAcpLock,
      ),
    [canBypassAcpLock, originalSimulation],
  )
  const maxFirstPaymentDate = useMemo(
    () => getLoanCalendarMaxPaymentDate(originalSimulation, frequency, getToday()),
    [originalSimulation, frequency],
  )
  const shouldDisableDate = useShouldDisableDateForNewFirstPayment(originalSimulation, frequency)

  const paymentConfigs = previewedSimulation?.simulation.fullLoan.paymentConfigs
  const newAmount = paymentConfigs?.length ? paymentConfigs[paymentConfigs.length - 1].amount : undefined

  return (
    <Stack spacing={2}>
      <Banner severity="info">{t('loan.payment.editPaymentFrequency.warningFirstPaymentDate')}</Banner>
      {maxFirstPaymentDate < minFirstPaymentDate && (
        <Banner severity="error">{t('loan.payment.editPaymentFrequency.errors.noValidDatesFound')}</Banner>
      )}

      <LoanCalendarActionPreviewBody
        simulationBefore={originalSimulation}
        preview={previewedSimulation}
        recomputing={isValid && previewedSimulation === null}
        actionDate={firstPaymentDate}
        error={error}
      />

      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledSelect
          name="frequency"
          control={control}
          options={frequencyOptions}
          label={t('loan.repaymentConfig.frequency')}
          disableEmptyOption
          disabled={disabled}
        />
        <ControlledDateOnlyPicker
          name="firstPaymentDate"
          control={control}
          label={t('loan.repaymentConfig.nextPaymentDate')}
          // Whether the new frequency is a real change is only decidable once there is a date
          // to read the current one at, so picking one re-decides the field above.
          onChange={() => void form.trigger('frequency')}
          disabled={disabled}
          shouldDisableDate={shouldDisableDate}
          minDate={minFirstPaymentDate}
          maxDate={maxFirstPaymentDate}
        />
      </FieldGrid>

      <BodyText>
        <Trans i18nKey="loan.payment.editPaymentFrequency.newAmount" values={{ amount: formatCurrency(newAmount) }} />
      </BodyText>
      <Banner severity="warning">{t('loan.payment.editPaymentFrequency.warning')}</Banner>
    </Stack>
  )
}

export const useLoanCalendarFrequencyDialog = (
  initialSignedSimulation: SignedCalendarSimulation,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()
  const frequencyOptions = useTranslatedPaymentFrequency()
  const schema = useNewFrequencySchema(initialSignedSimulation.simulation)
  const confirm = useCalendarPreviewConfirm(isLockedByOther)

  // Open on the frequency the loan is *not* on today, so the one field the dialog exists to
  // change never starts on the value that would be rejected as unchanged.
  const currentFrequency = initialSignedSimulation.simulation.getRepaymentConfigAtDate(getToday())?.frequency
  const frequency =
    (frequencyOptions.find((option) => option.key !== currentFrequency)?.key as EPaymentFrequency | undefined) ??
    EPaymentFrequency.Monthly

  return useFormDialog<NewFrequencySchemaType, void, SignedCalendarSimulation>({
    icon: CalendarSync,
    title: t('loan.payment.editPaymentFrequency.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    defaultValues: { frequency },
    // Confirming stages a pending change on the page, it saves nothing — the page says so.
    successMessage: false,
    ...confirm,
    content: ({ form }) => (
      <LoanCalendarFrequencyBody
        form={form}
        initialSignedSimulation={initialSignedSimulation}
        frequencyOptions={frequencyOptions}
        disabled={isLockedByOther}
      />
    ),
    // The preview is the submit: Confirm applies the recompute the agent watched settle
    // rather than firing a fresh one. The gate above never lets a null through.
    onSubmit: (values) => values.previewedSimulation ?? false,
  })
}
